import { createRef, CSSProperties, RefObject, useCallback, useEffect, useRef, useState } from 'react';

import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

const PLUS_MORE_WIDTH = 50;

type TruncatedListItem = { label: string; value: string | number };

type TTruncatedTextListProps = {
  items: TruncatedListItem[];
  width?: number;
  height?: number;
  placeholder?: string;
  gap?: number;
  containerStyle?: CSSProperties;
  placeholderStyles?: CSSProperties;
  plusMoreWidth?: number;
  displayAsString?: boolean; // if true, items will be displayed as comma delimited strings instead of Text components
};

const TruncatedTextList = ({
  gap = 4,
  containerStyle = {},
  placeholder,
  items,
  width: propsWidth,
  height: propsHeight,
  placeholderStyles = {},
  plusMoreWidth = PLUS_MORE_WIDTH,
}: TTruncatedTextListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const displayRefs = useRef<RefObject<HTMLDivElement | null>[]>(items?.map(() => createRef<HTMLDivElement>()) || []);

  const [displayItems, setDisplayItems] = useState<TruncatedListItem[]>(items || []);

  useEffect(() => {
    setDisplayItems(items);
  }, [items]);

  // we need to create refs for each item in the list
  useEffect(() => {
    displayRefs.current = displayItems.map(() => createRef<HTMLDivElement>());
  }, [displayItems]);

  const [remainingCount, setRemainingCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);

  const immediateParent = containerRef.current?.parentElement;
  const width = propsWidth || immediateParent?.offsetWidth;
  const height = propsHeight || immediateParent?.offsetHeight;

  const calculateAndSetDisplayCount = useCallback(() => {
    // calculate how many fit and add + <count> for the rest
    let totalWidth = 0;
    const maxWidth = (width || 0) - plusMoreWidth;
    let _displayCount = 0;

    const len = displayRefs.current.length;

    for (let i = 0; i < len; i++) {
      const el = displayRefs.current[i].current;
      if (!el) continue;

      // get width of the element
      const width = el?.offsetWidth || 0;
      totalWidth += width + (gap ?? 0); // add 8px for the gap
      if (totalWidth > maxWidth) {
        break;
      }
      _displayCount++;
    }

    setDisplayCount(_displayCount);
    setRemainingCount(len - (_displayCount || 1));

    // displayRefs.current is required to ensure that the elements are rendered before calculating the width
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRefs.current, gap, plusMoreWidth, width]);

  useEffect(() => {
    calculateAndSetDisplayCount();
  }, [calculateAndSetDisplayCount, displayItems]);

  return (
    <Container
      ref={containerRef}
      background="transparent"
      className="flex ai-center width-fit-content overflow-hidden"
      style={{
        maxWidth: `${width}px`,
        height: `${height}px`,
        gap: `${gap}px`,
        transition: 'opacity 0.2s ease-in-out',
        ...containerStyle,
      }}
    >
      {!!placeholder && !displayItems?.length ? (
        <Text
          className="S2"
          style={placeholderStyles}
          width={placeholderStyles?.width || 'fit-content'}
          color="var(--gray-300)"
        >
          {placeholder}
        </Text>
      ) : null}
      {displayItems?.length ? (
        <Container background="transparent" maxWidth={`${width}px`} width={`${width}px`} className="flex gap-0">
          {displayItems?.map((c, idx) => {
            return (
              <Text
                ref={displayRefs.current?.[idx]}
                whiteSpace={displayCount === 0 && idx === 0 ? undefined : 'nowrap'}
                key={c.value}
                width="fit-content"
                backgroundColor={'transparent'}
                className="radius-100 S3"
                clamp={displayCount === 0 && idx === 0 ? 1 : undefined}
                style={{
                  border: 'transparent',
                  ...(idx > 0 && idx >= displayCount
                    ? {
                        pointerEvents: 'none',
                        opacity: 0,
                        /* set non-visible elements to position absolute to prevent container width adjusting */
                        position: 'absolute',
                        visibility: 'hidden',
                      }
                    : {
                        opacity: 1,
                      }),
                }}
              >
                {idx === 0 ? c.label : `, ${c.label}`}
              </Text>
            );
          })}
          {remainingCount > 0 ? (
            <Container background="transparent" className="flex ai-center" style={{ width: `${plusMoreWidth}px` }}>
              <Text>,</Text>
              <Text
                whiteSpace="nowrap"
                width={`${plusMoreWidth}px`}
                className="S3 flex jc-center ai-center gradient-text as-one-element padding-l-2"
              >
                <Text
                  color={getThemeColorCSSVariable(EColorVariablesV2.primary)}
                  className="L2 flex fd-row ai-center gap-2"
                >
                  <Icon name={ICONS.ADD} size={10} />
                  <span>{remainingCount}</span>
                </Text>
              </Text>
            </Container>
          ) : null}
        </Container>
      ) : null}
    </Container>
  );
};

export default TruncatedTextList;
